import {
  App,
  createApp as createClientApp,
  h,
  onMounted,
  provide,
  resolveDynamicComponent,
  Transition,
  VNode,
} from 'vue'
import {RouterView, useRouter} from 'vue-router'
import {createHead} from '@vueuse/head'
import {createPinia} from 'pinia'
import {createI18n} from './i18n'
import {createRouter} from './router'

import {initUserSession, userSessionSymbol, checkIsInternalAdmin} from '/@src/composable/useUserSession'
import {apiSymbol, initApi, initGraphql} from '/@src/composable/useApi'
import useNotyf from '/@src/composable/useNotyf'
import {ApolloClients, DefaultApolloClient} from '@vue/apollo-composable'
import {getSelfInfo} from '/@src/api/authRepo'
import {fetchKV} from '/@src/api/configRepo'
import {iconfontInit} from '/@src/utils/iconfont'
import storage from '/@src/utils/storage'
import {getQueryString} from '/@src/utils'
import {client, clientOld} from "/@src/api/apolloClients";
import {createApiClients} from "/@src/api/apiClients";
import init from "/~/libs/format/common_format";

type VueroAppOptions = {
  enhanceApp?: (app: App) => Promise<void>
}

const stripCodeFromPath = (path: string) => {
  const url = new URL(path, window.location.origin)
  url.searchParams.delete('code')
  return `${url.pathname}${url.search}${url.hash}`
}

export async function createApp({ enhanceApp }: VueroAppOptions) {
  const head = createHead()
  const i18n = createI18n()
  const router = createRouter()
  const pinia = createPinia()
  const session = initUserSession()
  const apis = createApiClients(session)
  const gql = initGraphql(session)
  try {
    await init()
  }catch (e) {
    console.error(e)
  }

  /**
   * Here you can check if your user has a token stored
   * and check with your api if it still valid before your app start
   */
  if (session.isLoggedIn) {
    try {
      // do api request call to retreive its profile
      const user = await getSelfInfo(apis.default)
      if (!user.user) {
        session.token = ''
      } else {
        session.user = user.user
        // session.token =
        // userSession.token = api.token
        if (user.internal_info) {
          session.roles = user.internal_info.roles
        }
        // 拉取 KV admin 配置
        try {
          const kv = await fetchKV(apis.default, 'admin-access-list')
          session.adminIds = kv.value as number[] || []
        } catch (kvErr) {
          console.error('Failed to fetch admin-access-list KV:', kvErr)
          session.adminIds = []
        }
        session.isInternalAdmin = checkIsInternalAdmin(session)
      }
    } catch (err) {
      // delete stored token if it fails
      session.token = ''
    }
  }

  const app = createClientApp({
    // This is the global app setup function
    setup() {
      /**
       *
       * We provide user state and api to our entire app
       * We can then inject them later in any component
       *
       * @see /@src/composable/useApi and /@src/composable/useUserSession
       */
      provide(userSessionSymbol, session)
      provide(apiSymbol, apis)
      provide(DefaultApolloClient, gql)
      provide(ApolloClients, {
        default: client,
        clientOld,
      })


      /**
       * Here we are creating a render function for our router view with
       * a Transition for the inner component
       * Template equivalent would be:
       *
       * <RouterView v-slot="{ Component }">
       *   <transition name="fade-slow" mode="out-in">
       *     <component :is="Component" />
       *   </transition>
       * </RouterView>
       */
      onMounted(() => {
        iconfontInit()
        useRouter().resolve({

        })
        if (getQueryString('code')) {
          // 使用 nextTick 确保 Vue 应用完全渲染后再进行重定向
          // 使用 replaceState 替代 location.replace 避免页面重新加载
          const urlObj = new URL(window.location.href)
          const {origin, pathname, search} = urlObj
          if (search) {
            const params = new URLSearchParams(search)
            params.delete('code')
            const newSearch = params.toString()
            const newUrl = newSearch ? `${origin}${pathname}?${newSearch}` : `${origin}${pathname}`
            // 使用 History API 替换 URL，不会导致页面重新加载
            window.history.replaceState({}, '', newUrl)
          }
        }
      })
      return () => {
        const defaultSlot = ({ Component: _Component }: any) => {
          const Component = resolveDynamicComponent(_Component) as VNode
          return [
            h(
              Transition,
              { name: 'fade-slow', mode: 'out-in' },
              {
                default: () => [h(Component)],
              }
            ),
          ]
        }

        return h(RouterView, null, {
          default: defaultSlot,
        })
      }
    },
  })

  /**
   * Here we are setting up router navigation guards
   *
   * We can add meta to pages either by declaring them manualy in the
   * routes declaration (see /@src/router.ts)
   * or by adding a <route> tag into .vue files (see /@src/pages/sidebar/dashboards.ts)
   *
   * <route lang="yaml">
   * meta:
   *   requiresAuth: true
   * </route>
   *
   * <script setup lang="ts">
   *  // TS script
   * </script>
   *
   * <template>
   *  // HTML content
   * </template>
   */
  router.beforeEach((to, from) => {
    const authPageName = 'auth-login'
    if (to.name !== authPageName && !session.isLoggedIn) {
      const redirect = stripCodeFromPath(to.fullPath)
      storage.set('redirect', redirect)
      // this route requires auth, check if logged in
      // if not, redirect to login page.
      const notif = useNotyf()
      notif.error(
        'Sorry, you should loggin to access this section (anything will work)'
      )
      return {
        name: authPageName,
        // save the location we were at to come back later
        query: { redirect },
      }
    }

    // 已登录但非内部用户，只能访问白名单页面
    // 路由切换时check
    if (session.isLoggedIn) {
      const isAdmin = checkIsInternalAdmin(session)
      const externalAllowedRoutes = ['content-posts', 'auth-login']
      if (!isAdmin && !externalAllowedRoutes.includes(to.name as string)) {
        return { name: 'content-posts' }
      }
    }
  })

  app.use(head)
  app.use(router)
  app.use(i18n)
  app.use(pinia)

  if (enhanceApp) {
    await enhanceApp(app)
  }

  return app
}
